/* eslint-disable camelcase */
import { isDefaultCasino } from '../../utils/template';

function debounce(func: (...args: unknown[]) => unknown, wait: number) {
    let timeout: NodeJS.Timeout;
    return (...args: unknown[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

function escapeHtml(value: string): string {
    return value
        .split('&').join('&amp;')
        .split('<').join('&lt;')
        .split('>').join('&gt;')
        .split('"').join('&quot;')
        .split("'").join('&#039;');
}

document.querySelectorAll<HTMLElement>('.search-component').forEach((root) => {
    if (root.dataset.searchInitialized === 'true') {
        return;
    }

    root.dataset.searchInitialized = 'true';

    const searchInput = root.querySelector<HTMLInputElement>('.search-input');
    const searchIcon = root.querySelector<HTMLElement>('.search-icon');
    const searchResults = root.querySelector<HTMLElement>('.search-results');
    const clearBtn = root.querySelector<HTMLButtonElement>('.search-clear');
    const backdrop = root.querySelector<HTMLElement>('.casino-search-backdrop');
    const isCatalogSearch = root.classList.contains('search-component--catalog');
    let activeController: AbortController | null = null;
    let requestSequence = 0;
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

    if (!searchInput || !searchResults) return;

    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'search-results-loader';
    loadingOverlay.hidden = true;
    loadingOverlay.innerHTML = '<span class="search-results-loader__spinner" aria-hidden="true"></span>';
    searchResults.appendChild(loadingOverlay);

    const setOpen = (isOpen: boolean) => {
        root.classList.toggle('is-open', isOpen);

        if (backdrop) {
            backdrop.hidden = !isOpen;
        }
    };

    const setLoading = (isLoading: boolean) => {
        root.classList.toggle('is-loading', isLoading);
        loadingOverlay.hidden = !isLoading;
        loadingOverlay.style.display = isLoading ? 'flex' : 'none';

        if (isLoading) {
            if (!loadingOverlay.isConnected) {
                searchResults.appendChild(loadingOverlay);
            }
        } else if (loadingOverlay.isConnected) {
            loadingOverlay.remove();
        }
    };

    const clearMessageState = () => {
        searchResults.classList.remove('search-results--message');
        searchResults.textContent = '';
    };

    const showMessageState = (message: string) => {
        setOpen(true);
        setLoading(false);
        searchResults.innerHTML = '';
        searchResults.classList.add('search-results--message');
        searchResults.textContent = message;
    };

    const ensureMessageState = () => {
        const hasQuery = searchInput.value.trim().length > 0;
        const isOpen = root.classList.contains('is-open');
        const isLoading = root.classList.contains('is-loading');
        const hasElementChildren = searchResults.children.length > 0;
        const hasTextContent = searchResults.textContent?.trim().length;

        if (hasQuery && isOpen && !isLoading && !hasElementChildren && !hasTextContent) {
            showMessageState(getNoResultsLabel());
        }
    };

    const cancelActiveRequest = () => {
        activeController?.abort();
        activeController = null;
    };

    const clearFallbackTimeout = () => {
        if (!fallbackTimeoutId) return;

        clearTimeout(fallbackTimeoutId);
        fallbackTimeoutId = null;
    };

    const updateClearButton = () => {
        const hasQuery = searchInput.value.trim() !== '';

        root.classList.toggle('has-query', hasQuery);

        if (clearBtn) {
            clearBtn.hidden = !hasQuery;
        }
    };

    const updateCatalogGridLayout = (itemsCount: number) => {
        if (!isCatalogSearch) return;

        const mobileMinCardWidth = 120;
        const desktopMinCardWidth = 166;
        const gap = 12;
        const resultsWidth = searchResults.clientWidth || searchResults.getBoundingClientRect().width || 0;
        const minCardWidth = window.innerWidth <= 640 ? mobileMinCardWidth : desktopMinCardWidth;
        const maxColumns = Math.max(2, Math.floor((resultsWidth + gap) / (minCardWidth + gap)));

        if (itemsCount >= maxColumns) {
            searchResults.style.setProperty('--search-results-columns', `repeat(${maxColumns}, minmax(0, 1fr))`);
            searchResults.style.setProperty('--search-results-justify', 'stretch');
            return;
        }

        const visibleColumns = Math.max(2, itemsCount);
        searchResults.style.setProperty('--search-results-columns', `repeat(${visibleColumns}, ${minCardWidth}px)`);
        searchResults.style.setProperty('--search-results-justify', 'start');
    };

    const refreshCatalogGridLayout = () => {
        if (!isCatalogSearch) return;

        const itemsCount = searchResults.querySelectorAll('.search-results__item').length;
        updateCatalogGridLayout(itemsCount);
    };

    const showLoadingState = () => {
        clearMessageState();
        updateCatalogGridLayout(0);
        setOpen(true);
        searchResults.innerHTML = '';
        searchResults.appendChild(loadingOverlay);
        setLoading(true);
    };

    const getNoResultsLabel = () => {
        return 'No results found';
    };

    const renderEmptyState = () => {
        updateCatalogGridLayout(0);
        showMessageState(getNoResultsLabel());
    };

    const showEmptyState = () => {
        setOpen(true);
        setLoading(false);
        renderEmptyState();
    };

    const focusSearch = () => {
        searchInput.focus();
    };

    function redirectToSearchPage() {
        const query = searchInput.value.trim();

        const url = new URL(window.CASINO_SEARCH_URL, window.location.origin);
        url.searchParams.set('filters[name]', query);
        window.location.href = url.toString();
    }

    const renderResults = (data: Array<{
        image: string;
        name: string;
        creator_name: string;
        play_url: string;
        slug: string;
    }>) => {
        let html = '';

        if (isCatalogSearch) {
            data.forEach((game) => {
                const title = escapeHtml(game.name);
                const provider = escapeHtml(game.creator_name);
                const image = escapeHtml(game.image);
                const slug = escapeHtml(game.slug);

                html += `
                    <li class="search-results__item">
                        <button
                            type="button"
                            class="search-result-card"
                            data-slug="${slug}"
                            aria-label="${title}"
                        >
                            <span class="search-result-card__media">
                                <img src="${image}" alt="${title} | ${provider}" loading="lazy">
                            </span>
                            <span class="search-result-card__name">${title}</span>
                        </button>
                    </li>
                `;
            });
        } else {
            data.forEach((game) => {
                const title = escapeHtml(game.name);
                const provider = escapeHtml(game.creator_name);
                const image = escapeHtml(game.image);

                html += `
                    <li class="search-results__item row">
                        <img onerror="this.parentNode.style.display='none'"
                             src="${image}"
                             alt="${title} | ${provider}"/>
                        <div class="d-flex flex-column">
                            <span>${title}</span>
                            <span class="text-muted">${provider}</span>
                        </div>
                    </li>
                `;
            });
        }

        if (!html) {
            renderEmptyState();
            return;
        }

        searchResults.innerHTML = html;
        searchResults.classList.remove('search-results--message');
        updateCatalogGridLayout(data.length);

        if (isCatalogSearch) {
            searchResults.querySelectorAll<HTMLButtonElement>('.search-result-card').forEach((item) => {
                item.addEventListener('click', () => {
                    const slug = item.dataset.slug;

                    if (!slug) return;

                    const playGameUrl = window.CASINO_PLAY_URL.replace('slug', slug);
                    window.location.href = playGameUrl;
                });
            });
        } else {
            searchResults.querySelectorAll<HTMLElement>('.search-results__item').forEach((item, index) => {
                item.addEventListener('click', () => {
                    const playGameUrl = window.CASINO_PLAY_URL.replace('slug', data[index].slug);
                    window.location.href = playGameUrl;
                });
            });
        }
    };

    const debouncedSearchResults = debounce((query: string, requestId: number) => {
        updateClearButton();

        if (query.length === 0) {
            cancelActiveRequest();
            clearFallbackTimeout();
            clearMessageState();
            searchResults.innerHTML = '';
            setLoading(false);
            setOpen(false);
            return;
        }

        cancelActiveRequest();
        clearFallbackTimeout();
        activeController = new AbortController();
        fallbackTimeoutId = setTimeout(() => {
            if (requestId !== requestSequence) return;

            cancelActiveRequest();
            showEmptyState();
        }, 4000);

        fetch(`/casino/api/data/games?q=${encodeURIComponent(query)}`, {
            signal: activeController.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Search request failed with status ${response.status}`);
                }

                return response.json();
            })
            .then((dataResponse) => {
                if (requestId !== requestSequence) return;

                const data: Array<{
                    image: string;
                    name: string;
                    creator_name: string;
                    play_url: string;
                    slug: string;
                }> = Array.isArray(dataResponse.data) ? dataResponse.data : [];

                clearFallbackTimeout();
                activeController = null;
                setLoading(false);

                if (data.length === 0) {
                    showEmptyState();
                    window.setTimeout(ensureMessageState, 0);
                    return;
                }

                renderResults(data);
                window.setTimeout(ensureMessageState, 0);
            })
            .catch((error: unknown) => {
                if (requestId !== requestSequence) return;
                if (error instanceof DOMException && error.name === 'AbortError') return;

                clearFallbackTimeout();
                activeController = null;
                showEmptyState();
                window.setTimeout(ensureMessageState, 0);
            });
    }, 800);

    searchInput.addEventListener('input', () => {
        const query = searchInput.value.trim();

        updateClearButton();

        if (query.length === 0) {
            cancelActiveRequest();
            clearFallbackTimeout();
            clearMessageState();
            searchResults.innerHTML = '';
            setLoading(false);
            setOpen(false);
            return;
        }

        requestSequence += 1;
        showLoadingState();
        debouncedSearchResults(query, requestSequence);
    });

    searchInput.addEventListener('focus', () => {
        const query = searchInput.value.trim();

        if (isCatalogSearch) {
            if (query.length > 0) {
                setOpen(true);
            }
        } else {
            setOpen(true);
        }

        if (isCatalogSearch && query.length > 0 && !searchResults.children.length && !root.classList.contains('is-loading')) {
            requestSequence += 1;
            showLoadingState();
            debouncedSearchResults(query, requestSequence);
        }

        updateClearButton();
    });

    searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            cancelActiveRequest();
            clearFallbackTimeout();
            setOpen(false);
            setLoading(false);
            clearMessageState();
            searchResults.innerHTML = '';
            searchInput.blur();
            return;
        }

        if (e.key === 'Enter') {
            redirectToSearchPage();
        }
    });

    searchIcon?.addEventListener('click', () => {
        if (isCatalogSearch) {
            focusSearch();
            return;
        }

        redirectToSearchPage();
    });

    clearBtn?.addEventListener('click', () => {
        cancelActiveRequest();
        clearFallbackTimeout();
        searchInput.value = '';
        clearMessageState();
        searchResults.innerHTML = '';
        updateClearButton();
        setLoading(false);
        setOpen(!isCatalogSearch);
        searchInput.focus();
    });

    backdrop?.addEventListener('click', () => {
        cancelActiveRequest();
        clearFallbackTimeout();
        setOpen(false);
        setLoading(false);
        clearMessageState();
    });

    window.addEventListener('resize', refreshCatalogGridLayout);

    updateClearButton();
    if (searchInput.value.trim() !== '') {
        setOpen(true);
        requestSequence += 1;
        showLoadingState();
        debouncedSearchResults(searchInput.value.trim(), requestSequence);
    }
});
